import React, { useState, useEffect } from 'react';
import { Card, Button } from '@shopify/polaris';
import { config } from '../constants';
import { authenticatedFetch } from '../utils/api';

const API_BASE = config.API_BASE;
const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect (User Language)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'zh-Hans', label: 'Chinese (Simplified)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  // Add more as needed
];

export default function LanguageCard({ storeId }) {
  const [adminLang, setAdminLang] = useState('auto');
  const [langSaveState, setLangSaveState] = useState('');

  // Fetch admin language from backend settings on mount or storeId change
  useEffect(() => {
    if (!storeId) return;
    (async () => {
      try {
        const res = await authenticatedFetch(`${API_BASE}/settings?shop=${storeId}`);
        if (res.ok) {
          const data = await res.json();
          setAdminLang(data.language || 'auto');
        }
      } catch {
        setAdminLang('auto');
      }
    })();
  }, [storeId]);

  return (
    <Card title="Language & Translation" sectioned>
      <div style={{ marginBottom: 18 }}>
        {langSaveState === 'success' && (
          <div style={{ color: '#0a0', background: '#e6ffe6', borderRadius: 8, padding: '8px 16px', margin: '18px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span role="img" aria-label="success">✅</span> Language saved!
          </div>
        )}
        {langSaveState === 'error' && (
          <div style={{ color: '#b00', background: '#ffeaea', borderRadius: 8, padding: '8px 16px', margin: '18px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span role="img" aria-label="error">⚠️</span> Failed to save language.
          </div>
        )}
        <label htmlFor="default-lang-select" style={{ fontWeight: 500, marginBottom: 6, display: 'block' }}>
          Default language for live chat admin:
        </label>
        <select
          id="default-lang-select"
          value={adminLang}
          onChange={e => setAdminLang(e.target.value)}
          style={{ padding: '8px 12px', fontSize: 20, borderRadius: 6, border: '1px solid #ddd', minWidth: 180, marginBottom: 12 }}
        >
          {LANGUAGE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div style={{ marginTop: 12 }}>
          <Button variant="primary"
            onClick={async () => {
              setLangSaveState('saving');
              try {
                const res = await authenticatedFetch(`${API_BASE}/settings`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ shop: storeId, settings: { language: adminLang } })
                });
                if (res.ok) {
                  setLangSaveState('success');
                  setTimeout(() => setLangSaveState(''), 2500);
                } else {
                  setLangSaveState('error');
                  setTimeout(() => setLangSaveState(''), 2500);
                }
              } catch {
                setLangSaveState('error');
                setTimeout(() => setLangSaveState(''), 2500);
              }
            }}
            disabled={langSaveState === 'saving'}
            primary
          >
            Save Language
          </Button>
        </div>
      </div>
      <div style={{ color: '#555', fontSize: 15, marginBottom: 12, marginTop: 18 }}>
        Incoming live chat messages will be translated to this language for the admin. The user will always see responses in their own language.
      </div>
      <div style={{ color: '#888', fontSize: 14 }}>
        (Feature preview: Changing this setting only affects new live chat sessions.)
      </div>
    </Card>
  );
}
